/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import {
  Button,
  ButtonSize,
  ButtonVariety,
  DropdownMenu,
  IconChevronDown,
  Text,
  TextSize,
} from '@sonarsource/echoes-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { JiraWorkType } from '../../types';
import JiraIcon from '../JiraIcon';

export interface JiraWorkItemCreationButtonProps {
  isLoading: boolean;
  jiraWorkTypes: JiraWorkType[];
  onCreateWorkItem: (workTypeId: string) => void;
}

export function JiraWorkItemCreationButton({
  isLoading,
  jiraWorkTypes,
  onCreateWorkItem,
}: Readonly<JiraWorkItemCreationButtonProps>) {
  const selectedWorkTypes = jiraWorkTypes.filter((workType) => workType.selected);
  const { formatMessage } = useIntl();

  const buttonLabel = isLoading
    ? formatMessage({ id: 'issue.jira.work_item_creation.in_progress' })
    : formatMessage({ id: 'issue.jira.work_item_creation' });

  if (selectedWorkTypes.length === 0) {
    return null;
  }

  if (selectedWorkTypes.length === 1) {
    return (
      <Button
        isDisabled={isLoading}
        isLoading={isLoading}
        onClick={() => {
          onCreateWorkItem(selectedWorkTypes[0].id);
        }}
        prefix={<JiraIcon />}
        size={ButtonSize.Medium}
        variety={ButtonVariety.DefaultGhost}
      >
        <Text size={TextSize.Default}>{buttonLabel}</Text>
      </Button>
    );
  }

  return (
    <DropdownMenu items={getDropdownItems(jiraWorkTypes, onCreateWorkItem)}>
      <Button
        isDisabled={isLoading}
        isLoading={isLoading}
        prefix={<JiraIcon />}
        suffix={<IconChevronDown />}
        variety={ButtonVariety.DefaultGhost}
      >
        <Text size={TextSize.Default}>{buttonLabel}</Text>
      </Button>
    </DropdownMenu>
  );
}

function getDropdownItems(
  jiraWorkTypes: JiraWorkType[],
  createJiraWorkItem: (workTypeId: string) => void,
) {
  return (
    <>
      <DropdownMenu.GroupLabel>
        <FormattedMessage id="issue.jira.work_type_dropdown" />
      </DropdownMenu.GroupLabel>
      {jiraWorkTypes
        .filter((workType) => workType.selected)
        .map((workType) => (
          <DropdownMenu.ItemButton
            key={workType.id}
            onClick={() => {
              createJiraWorkItem(workType.id);
            }}
          >
            {workType.name}
          </DropdownMenu.ItemButton>
        ))}
    </>
  );
}
